import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, Sun, Moon, Code2 } from 'lucide-react'
import { useTheme } from '../hooks/useTheme'

const navLinks = [
  { path: '/', label: 'Home' },
  { path: '/archive', label: 'The Archive' },
  { path: '/decode', label: 'The Decode' },
  { path: '/forge', label: 'The Forge' },
]

export default function Navbar() {
  const { theme, toggleTheme } = useTheme()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => { setMobileOpen(false) }, [location])

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-ink/80 backdrop-blur-2xl border-b border-ink-border'
          : 'bg-transparent'
      }`}
    >
      <div className="section-base">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo — minimal, editorial */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-9 h-9 rounded border border-copper/40 flex items-center justify-center text-copper font-bold text-sm font-mono tracking-wider group-hover:border-copper/80 transition-colors duration-300">
              QA
            </div>
            <span className="font-display font-bold text-lg tracking-tight text-cream">
              Quant<span className="text-copper">Algo</span>Labs
            </span>
          </Link>

          {/* Desktop Navigation — thin, elegant */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium tracking-wide transition-colors duration-300 py-2 ${
                  location.pathname === link.path
                    ? 'text-cream'
                    : 'text-cream-muted hover:text-cream'
                }`}
              >
                {link.label}
                {location.pathname === link.path && (
                  <motion.div
                    layoutId="navbar-indicator"
                    className="absolute -bottom-0.5 left-0 right-0 h-px bg-copper"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
              </Link>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded border border-cream-dim/30 flex items-center justify-center text-cream-muted hover:text-cream hover:border-cream-dark/50 transition-all duration-300"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden w-9 h-9 rounded border border-cream-dim/30 flex items-center justify-center text-cream"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="lg:hidden bg-ink/95 backdrop-blur-2xl border-b border-ink-border"
          >
            <div className="section-base py-6 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded text-sm font-medium tracking-wide transition-colors ${
                    location.pathname === link.path
                      ? 'text-copper border-l border-copper bg-copper/5'
                      : 'text-cream-muted hover:text-cream hover:bg-ink-elevated'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}
